import {
  ALERT_ID,
  createAlertTracker,
  type AlertShowing
} from '#~/analytics/domains/alert-lifecycle.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { broker } from '#~/agent/broker.js';
import { useAppVersion } from '#~/agent/hooks.js';
import type { Proxy } from '#~/agent/types.js';
import { ConnectionStatus } from '#~/common/connection-status/index.js';
import { config } from '#~/config/index.js';
import { desktopUpdateIdleLabel } from '#~/electron/update-check-status.js';
import { isElectronApp } from '#~/electron/utils.js';
import type { NewtsConnection } from '#~/newts/connection.js';
import { isMultiBridgeAvailable, PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
// Per IR-104 plan W1 acceptance criterion #3: stable data selector for the
// agent-version label so the Playwright e2e harness can assert on the
// state-sync-sourced version rather than the useAppVersion() fallback. The
// harness reads dataSTag(Sel.BridgeConnection, 'agent-version').
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { Button } from '@studio/design-system/button';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { Agent } from '@studio/models/agent';
import { StatusUpdateState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { isNotNil } from '@tryiris-ai/js-types';
import { isBridgeDetailAvailable } from '../detail-availability.js';
import { clsx } from 'cn';
import prettyBytes from 'pretty-bytes';
import { useEffect, type ReactElement } from 'react';
import { Name } from '../../monitoring/agent/name.js';
import type { AgentStats } from '../../monitoring/types.js';
import { toAgentStats } from '../../monitoring/utils.js';
import { useGetMachineAgentId, usePermissions, useRoomAgent } from '../hooks.js';
import { BridgeAddButton } from './bridge-add-button.js';
import { BridgeDropdown } from './bridge-dropdown.js';
import { useDeviceImage, type AgentSystemInfo } from './device-img.js';

type Stats = AgentStats | undefined;

interface ConnectedBridgeProps {
  roomAgent: Exclude<ReturnType<typeof useRoomAgent>, undefined>;
  onSearch?: () => void;
  machineName: string;
  isPtzoptics: boolean;
  agents: { id: Agent['id']; name: Agent['name']; status: Agent['status'] }[];
  agentSystemInfo: Record<Agent['id'], AgentSystemInfo>;
  selectedAgentId: Agent['id'] | null;
  unavailableShowings: Map<Agent['id'], AlertShowing | null>;
  showUnavailable: boolean;
  panelOpen: boolean;
}
/** `ConnectedBridge` component */
export const ConnectedBridge = ({
  agents,
  roomAgent,
  onSearch,
  machineName,
  isPtzoptics,
  agentSystemInfo,
  selectedAgentId,
  unavailableShowings,
  showUnavailable,
  panelOpen
}: ConnectedBridgeProps): ReactElement => {
  const { track } = useAnalytics();
  const { enabled: isBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE);
  const { enabled: isMultiBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.MULTIBRIDGE_FEATURE);
  const canUseMultiBridge = isMultiBridgeAvailable(
    config.vendor,
    isBridgeEnabled,
    isMultiBridgeEnabled
  );

  const { canSwitchBridge, canUnlink } = usePermissions();
  const appVersion = useAppVersion();

  const { agentId: machineAgentId } = useGetMachineAgentId();
  const sameAgent = !selectedAgentId && machineAgentId === roomAgent.agent.id;

  // Get the effective agent ID - use selectedAgentId if available, otherwise use the room agent
  const effectiveAgentId = selectedAgentId ?? roomAgent.agent.id;

  // Associated proxy for the effective agent
  const proxy: Proxy | undefined = broker.acquire(effectiveAgentId);

  const machineNameToDisplay = (() => {
    if (selectedAgentId) {
      const name = agents.find((agent) => agent.id === selectedAgentId)?.name ?? machineName;
      return name;
    }
    return machineName;
  })();

  // Use unified device image logic
  const deviceImage = useDeviceImage(
    agentSystemInfo[effectiveAgentId]
      ? {
          agentId: effectiveAgentId,
          systemInfo: agentSystemInfo[effectiveAgentId]
        }
      : {
          agentId: effectiveAgentId
        }
  );

  const stateUpdateStatus = proxy?.state?.status?.update;
  const updateStatusForSelectedAgent = {
    updateAvailable: [
      StatusUpdateState.UPDATE_STATE_UPDATE_AVAILABLE,
      StatusUpdateState.UPDATE_STATE_DOWNLOADING,
      StatusUpdateState.UPDATE_STATE_WAITING_TO_INSTALL
    ].includes(stateUpdateStatus?.state ?? StatusUpdateState.UPDATE_STATE_IDLE),
    isDownloading: stateUpdateStatus?.state === StatusUpdateState.UPDATE_STATE_DOWNLOADING,
    isReadyToInstall:
      stateUpdateStatus?.state === StatusUpdateState.UPDATE_STATE_WAITING_TO_INSTALL,
    isInstalling: stateUpdateStatus?.state === StatusUpdateState.UPDATE_STATE_INSTALLING,
    bytesDownloaded: stateUpdateStatus?.bytesDownloaded ?? 0,
    bytesTotal: stateUpdateStatus?.bytesTotal ?? 0,
    bytesPerSecond: stateUpdateStatus?.bytesPerSecond ?? 0
  };

  const stats: Stats = (() => {
    if (selectedAgentId) {
      return toAgentStats(proxy?.state.status);
    }
    return roomAgent.agentStats;
  })();

  const hasMissingDetails =
    !isBridgeDetailAvailable(stats?.cpuUsage) ||
    !isBridgeDetailAvailable(stats?.ramUsage) ||
    !isBridgeDetailAvailable(stats?.diskSpace);
  const studioId = roomAgent.wiring.room?.id;

  useEffect(() => {
    if (!panelOpen || !showUnavailable) return;
    const tracker = createAlertTracker(track);
    if (hasMissingDetails && !unavailableShowings.has(effectiveAgentId)) {
      const next: AlertShowing = {
        alert_id: ALERT_ID.N1,
        showing_id: crypto.randomUUID(),
        agent_id: effectiveAgentId,
        ...(studioId ? { studio_id: studioId } : {})
      };
      unavailableShowings.set(effectiveAgentId, next);
      tracker.trackAlertShown(next);
    }
  }, [
    panelOpen,
    showUnavailable,
    hasMissingDetails,
    effectiveAgentId,
    studioId,
    track,
    unavailableShowings
  ]);

  const missingValue = showUnavailable ? 'Not available' : '—';

  const updateStatus = (() => {
    if (selectedAgentId) {
      return updateStatusForSelectedAgent;
    }
    return roomAgent.updateStatus;
  })();

  const onlineStatus = (() => {
    // First check the proxy status for real-time connection state
    const proxyStatus = proxy?.getStatus();
    if (proxyStatus === 'online') {
      return true;
    }

    // Fallback to agent status from the depot for more reliable status
    const agentFromList = selectedAgentId
      ? agents.find((agent) => agent.id === selectedAgentId)
      : agents.find((agent) => agent.id === roomAgent.agent.id);

    if (agentFromList) {
      return (
        agentFromList.status === 'online' ||
        agentFromList.status === 'idle' ||
        agentFromList.status === 'booting'
      );
    }

    // Final fallback - return false if no proxy or not online
    return false;
  })();

  const connectionMode = (() => {
    const newts = proxy?.connection as NewtsConnection;
    return newts?.connectedMode;
  })();

  const agentActions = (() => {
    if (selectedAgentId) {
      const wiring = roomAgent.wiring;
      return {
        restart: () => wiring?.handleRestart(selectedAgentId),
        unlink: () => wiring?.handleUnlink(selectedAgentId)
      };
    }
    return {
      restart: roomAgent.restart,
      unlink: roomAgent.unlink
    };
  })();

  // Function to convert bytes to MB
  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  // Convert download speed from bytes per second to Mbps
  const bitsToMbps = (bytesPerSecond: number) => (bytesPerSecond / (1024 * 1024)).toFixed(2);

  return (
    <div>
      {isBridgeEnabled && canSwitchBridge && (
        <div className='flex items-stretch justify-between gap-8 p-16'>
          <BridgeDropdown
            agents={agents}
            machineName={machineNameToDisplay}
            machineAgentId={machineAgentId}
            isPtzoptics={isPtzoptics}
            onSearch={() => onSearch?.()}
            onlineStatus={onlineStatus}
            showLinkAnotherBridge={canUseMultiBridge}
          />
          {isElectronApp() && canUseMultiBridge && (
            <BridgeAddButton onSearch={() => onSearch?.()} isPtzoptics={isPtzoptics} />
          )}
        </div>
      )}
      {/* Divider */}
      <div
        className={clsx('border-b-1', {
          'border-base-50': !isPtzoptics,
          'border-[#46464B]': isPtzoptics
        })}
      />
      {/* Device Image */}
      <div className='p-16 text-center'>
        <img
          src={deviceImage}
          className='mx-auto mt-8 w-[120px]'
          data-stag='bridge.device_image.unified'
        />
        <div className='mt-8 flex justify-center'>
          {/*
            The connected/disconnected state is copy inside one badge, so the
            uniqueID carries the state: tests wait for
            `bridge-connection.connection-status:online` instead of matching
            the word "Online". Per rule: playwright-stag-selectors-only
            (ai.knowledge/platform).
          */}
          <div
            className={clsx(
              'rounded-8 px-12 py-6',
              'flex items-center gap-4 text-sm font-medium',
              'transition-colors duration-750',
              'bg-base-300'
            )}
            {...DataSTag(
              Sel.BridgeConnection,
              'connection-status',
              onlineStatus ? 'online' : 'offline'
            )}
          >
            <ConnectionStatus connected={onlineStatus} className='text-[6px]' />
            {onlineStatus ? `Online (${connectionMode})` : 'Offline'}
          </div>
        </div>
        <div className='mt-12 flex justify-center'>
          <Name
            agentId={effectiveAgentId}
            wiring={roomAgent.wiring}
            machineName={machineNameToDisplay}
          />
        </div>
        {sameAgent && <p className='my-0 mt-4 text-muted'>(This Computer)</p>}
        {/* update status */}
        <div className='mt-20 flex justify-center gap-12'>
          {updateStatus?.isDownloading && (
            <div className='gap-12'>
              <div className='mb-12 h-[6px] w-full rounded-full bg-base-300'>
                <div
                  className='h-[100%] rounded-full bg-primary'
                  style={{
                    width: `${(updateStatus.bytesDownloaded / updateStatus.bytesTotal) * 100}%`
                  }}
                />
              </div>
              <div className='text-sm text-muted'>
                {bytesToMB(updateStatus.bytesDownloaded)} of {bytesToMB(updateStatus.bytesTotal)} MB
                downloaded ({bitsToMbps(updateStatus.bytesPerSecond)} Mbps)
              </div>
            </div>
          )}
          {/* Actions to restart or unlink the agent */}
          {!updateStatus?.isDownloading && (
            <div className='flex justify-center gap-12'>
              <Button onClick={agentActions.restart} size='lg' className='flex items-center gap-4'>
                <span>Restart</span>
              </Button>
              {isBridgeEnabled && (
                <Button
                  variant='secondary'
                  size='lg'
                  disabled={!canUnlink}
                  onClick={agentActions.unlink}
                >
                  Unlink
                </Button>
              )}
            </div>
          )}
        </div>
        {/* encoder stats */}
        <div className='mt-24 rounded-4 bg-base-200'>
          <div className='flex text-left'>
            <div className='flex flex-1 flex-col gap-8 border-r-1 border-base-100 p-16'>
              <div className='text-sm text-muted'>CPU Usage</div>
              <div
                {...DataSTag(Sel.BridgeConnection, 'cpu-usage')}
                className={clsx({
                  'text-muted': showUnavailable && !isBridgeDetailAvailable(stats?.cpuUsage)
                })}
              >
                {isNotNil(stats?.cpuUsage) &&
                (!showUnavailable || isBridgeDetailAvailable(stats.cpuUsage))
                  ? `${stats.cpuUsage}%`
                  : missingValue}
              </div>
            </div>
            <div className='flex flex-1 flex-col gap-8 border-r-1 border-base-100 p-16'>
              <div className='text-sm text-muted'>RAM Usage</div>
              <div
                {...DataSTag(Sel.BridgeConnection, 'ram-usage')}
                className={clsx({
                  'text-muted': showUnavailable && !isBridgeDetailAvailable(stats?.ramUsage)
                })}
              >
                {isNotNil(stats?.ramUsage) &&
                (!showUnavailable || isBridgeDetailAvailable(stats.ramUsage))
                  ? `${stats.ramUsage}%`
                  : missingValue}
              </div>
            </div>
            <div className='flex flex-1 flex-col gap-8 p-16'>
              <div className='text-sm text-muted'>Disk Space</div>
              <div
                {...DataSTag(Sel.BridgeConnection, 'disk-space')}
                className={clsx({
                  'text-muted': showUnavailable && !isBridgeDetailAvailable(stats?.diskSpace)
                })}
              >
                {isNotNil(stats?.diskSpace) &&
                (!showUnavailable || isBridgeDetailAvailable(stats.diskSpace))
                  ? `${prettyBytes(stats.diskSpace, { binary: false })}`
                  : missingValue}
              </div>
            </div>
          </div>
          <div className='flex items-center justify-between border-t-1 border-base-100 px-16 py-10 text-sm text-muted'>
            {/*
              Per IR-104 plan W1: DataSTag marks this div as the agent-version
              data anchor. `stats?.version` is the embedded encoder's reported
              version via state-sync (proxy.state.status.encoder.version);
              the appVersion fallback fires only when the encoder hasn't
              reported yet. The Step-5 Online gate in the e2e spec is the
              primary defence against reading the fallback path.
            */}
            <div {...DataSTag(Sel.BridgeConnection, 'agent-version')}>
              {vendorName()} version: {stats?.version ?? appVersion}
            </div>
            {desktopUpdateIdleLabel(updateStatus ?? {}) === 'check-failed' && (
              <div className='text-muted'>Couldn't check for updates</div>
            )}
            {desktopUpdateIdleLabel(updateStatus ?? {}) === 'up-to-date' && (
              <div className='text-muted'>Up To Date</div>
            )}

            {/*
              One tag per update phase: the e2e suite walks offer → download →
              ready-to-install by STag, so the button copy stays a product
              decision rather than a test contract.
              Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
            */}
            {updateStatus?.updateAvailable &&
              !updateStatus.isDownloading &&
              !updateStatus.isReadyToInstall && (
                <Button
                  variant='link'
                  onClick={roomAgent.update}
                  {...STagNew(Sel.BridgeConnection, 'update')}
                >
                  Update
                </Button>
              )}

            {updateStatus?.updateAvailable && updateStatus.isDownloading && (
              <Button variant='link' disabled {...STagNew(Sel.BridgeConnection, 'downloading')}>
                Downloading...
              </Button>
            )}

            {updateStatus?.updateAvailable && updateStatus.isReadyToInstall && (
              <Button
                variant='link'
                onClick={roomAgent.update}
                {...STagNew(Sel.BridgeConnection, 'restart-install')}
              >
                Restart & Install
              </Button>
            )}

            {updateStatus?.isInstalling && (
              <Button variant='link' disabled {...STagNew(Sel.BridgeConnection, 'installing')}>
                Installing...
              </Button>
            )}
          </div>
        </div>
        {/* install agent */}
        {!isElectronApp() && (
          <div className='mt-12 flex justify-center'>
            <a href='/agent/setup' target='_blank' className='mx-auto no-underline'>
              <Button variant='link' className='text-sm font-medium'>
                Need to install {vendorName()}? Click here
              </Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
